// packages/shared-hooks/src/leads/keys.ts
//
// Query-key factory for the Leads aggregate (#1081). Centralised so every
// mutation (create, patch, stage transition, claim, assign, archive,
// restore) invalidates the SAME prefixes the list / detail hooks read.
// Mirrors `clientsKeys` (#601).
//
// Shapes:
//   - `lists()` / `list(filter)` — single-page list slot. `lists()` is the
//     prefix mutations use to flush every filtered variant in one call.
//   - `infinite(filter)` — `useInfiniteQuery` keeps `InfiniteData` under its
//     own slot so list-shape branching is unnecessary.
//   - `details()` / `detail(id)` — single-lead read; `details()` is a
//     prefix-only key.
//   - `intakeHistory(id)` — external call-center intake submissions for a
//     single lead (#1260); read-only, no mutation flushes it.
//   - `myCallCenter()` / `myCallCenterInfinite(filter)` — the external
//     call-center agent's OWN accepted submissions (#1396). `myCallCenter()`
//     is the prefix the submit mutation flushes so a new lead appears; the
//     `filter` (phone search) is folded into the infinite slot key so a
//     search refetches a fresh cursor walk.
export const leadsKeys = {
  all: ['leads'] as const,
  lists: () => ['leads', 'list'] as const,
  list: (filter: Record<string, unknown>) => ['leads', 'list', filter] as const,
  infinite: (filter: Record<string, unknown>) => ['leads', 'infinite', filter] as const,
  details: () => ['leads', 'detail'] as const,
  // WHY (#1598): the `with-count` suffix is appended ONLY when the detail
  //   surface opts into `?includeRequestCount=true`, so the DEFAULT
  //   `detail(id)` key stays byte-for-byte unchanged (backward compat for
  //   every existing reader/invalidator). Prefix-based
  //   `invalidateQueries(['leads','detail',id])` still matches both variants.
  detail: (id: string, opts?: { includeRequestCount?: boolean }) =>
    opts?.includeRequestCount
      ? (['leads', 'detail', id, 'with-count'] as const)
      : (['leads', 'detail', id] as const),
  intakeHistory: (id: string) => ['leads', 'intake-history', id] as const,
  // WHY (#1598): keyed under the lead's detail prefix so a prefix
  //   invalidation of the lead flushes its linked-request rows too.
  requests: (id: string) => ['leads', 'detail', id, 'requests'] as const,
  // WHY (#2314): keyed under the lead's detail prefix so a prefix
  //   invalidation of the lead flushes its linked-artifact rows too.
  artifacts: (id: string) => ['leads', 'detail', id, 'artifacts'] as const,
  assignableManagers: () => ['leads', 'assignable-managers'] as const,
  myCallCenter: () => ['leads', 'my-call-center'] as const,
  myCallCenterInfinite: (filter: Record<string, unknown>) =>
    ['leads', 'my-call-center', 'infinite', filter] as const,
};
